import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};
const emptyStr = (v) => (v === '' || v == null ? null : v);

const defaultForm = {
  bankName: '',
  bankCode: '',
  bankShortName: '',
  swiftBic: '',
  country: '',
  website: '',
  status: 'ACTIVE',
};

const mapBankRow = (item) => ({
  id: item.bankId,
  bankName: item.bankName,
  bankCode: item.bankCode,
  bankShortName: item.bankShortName,
  swiftBic: item.swiftBic,
  country: item.country,
  website: item.website,
  status: item.status,
  raw: item,
});

const BankMaster = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.BANK_MASTER);
  const tenantId = getTenantId();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const fetchBanks = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`conductor/cash-recon/banks/by-tenant?tenant_id=${tenantId}`);
      setRows((Array.isArray(data) ? data : []).map(mapBankRow));
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load banks');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  const columns = useMemo(() => [
    { field: 'bankName', headerName: 'Bank Name', width: 220, resizable: true },
    { field: 'bankCode', headerName: 'Bank Code', width: 120, resizable: true },
    { field: 'bankShortName', headerName: 'Short Name', width: 120, resizable: true },
    { field: 'swiftBic', headerName: 'SWIFT/BIC', width: 120, resizable: true },
    { field: 'country', headerName: 'Country', width: 90, resizable: true },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      resizable: true,
      renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
    },
  ], []);

  const buildPayload = () => ({
    tenantId,
    bankName: form.bankName?.trim(),
    bankCode: emptyStr(form.bankCode),
    bankShortName: emptyStr(form.bankShortName),
    swiftBic: emptyStr(form.swiftBic),
    country: emptyStr(form.country),
    website: emptyStr(form.website),
    status: form.status || 'ACTIVE',
  });

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setForm({
      bankName: selectedRow.bankName || '',
      bankCode: selectedRow.bankCode || '',
      bankShortName: selectedRow.bankShortName || '',
      swiftBic: selectedRow.swiftBic || '',
      country: selectedRow.country || '',
      website: selectedRow.website || '',
      status: selectedRow.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    try {
      setSubmitting(true);
      const userId = getUserId();
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/cash-recon/banks', {
          method: 'POST',
          body: JSON.stringify({ ...payload, createdBy: userId, updatedBy: userId }),
        });
        showSuccess('Bank Master saved successfully');
      } else {
        await apiFetch(`conductor/cash-recon/banks/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify({ ...payload, updatedBy: userId }),
        });
        showSuccess('Bank Master updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchBanks();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/cash-recon/banks/soft/${rowPendingDelete.id}`, { method: 'DELETE' });
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      fetchBanks();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>Bank Master</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit Bank Master' : 'No edit permission'}><span><IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canAdd ? 'Add Bank Master' : 'No add permission'}><span><IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}><AddIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canDelete ? 'Delete Bank Master' : 'No delete permission'}><span><IconButton onClick={() => { setRowPendingDelete(selectedRow); setDeleteOpen(true); }} disabled={!selectedRow || !canDelete} sx={harmonatePlainIconButtonSx}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro rows={rows} columns={columns} loading={loading} rowHeight={40} pagination pageSizeOptions={[10, 25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 100 } } }} rowSelectionModel={selectionModel} onRowSelectionModelChange={setSelectionModel} sx={{ ...harmonateDataGridSx, flex: 1, minHeight: 0, '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' } }} />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Bank Master' : 'Edit Bank Master'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" required label="Bank Name" value={form.bankName} onChange={(e) => setForm((p) => ({ ...p, bankName: e.target.value }))} />
            <TextField fullWidth size="small" label="Bank Code" value={form.bankCode} onChange={(e) => setForm((p) => ({ ...p, bankCode: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" label="Short Name" value={form.bankShortName} onChange={(e) => setForm((p) => ({ ...p, bankShortName: e.target.value }))} />
            <TextField fullWidth size="small" label="SWIFT/BIC" value={form.swiftBic} onChange={(e) => setForm((p) => ({ ...p, swiftBic: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" label="Country (2-letter)" value={form.country} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))} />
            <TextField fullWidth size="small" label="Website" value={form.website} onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" select label="Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>{submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}><Typography sx={{ color: ht.text }}>Are you sure you want to delete &quot;{rowPendingDelete?.bankName || 'selected'}&quot;?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>{deleteLoading ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} title={toastState.title} autoHideDuration={toastState.autoHideDuration} position={toastState.position} />
    </Box>
  );
};

export default BankMaster;
